import type {
  V2AdminCreditDetailDto,
  V2AdminCreditsListDto,
  V2AdminCreditsSummaryDto
} from '@idc-forms/api-client'
import type { CreditRowStatus, FinancingType } from '@idc-forms/schemas/api-v2/admin-credits'

export const CREDIT_STAGE_DEFINITIONS = [
  { key: 'initial_contact', name: 'Inicio y contacto a cliente', shortName: 'Inicio' },
  { key: 'file_integration', name: 'Integración de expediente', shortName: 'Expediente' },
  { key: 'appraisals', name: 'Avalúos', shortName: 'Avalúos' },
  { key: 'financial_validation', name: 'Validación financiera', shortName: 'Validación' },
  { key: 'invoicing', name: 'Facturación', shortName: 'Facturación' },
  { key: 'formalization', name: 'Formalización y escrituración', shortName: 'Formalización' },
  { key: 'disbursement', name: 'Desembolso y entrega', shortName: 'Desembolso' }
] as const

export type CreditStageKey = (typeof CREDIT_STAGE_DEFINITIONS)[number]['key']

export const CREDIT_SORT_FIELDS = [
  'code',
  'status',
  'currentStageKey',
  'financingType',
  'lastPaymentDate',
  'startsAt',
  'propertyName',
  'projectName',
  'creditCode'
] as const

export type CreditSortField = (typeof CREDIT_SORT_FIELDS)[number]
export type CreditSort = `${CreditSortField}:${'asc' | 'desc'}`
export const CREDITS_DEFAULT_SORT: CreditSort = 'code:desc'

export const CREDIT_ROW_STATUS_LABELS: Record<CreditRowStatus, string> = {
  not_started: 'No iniciado',
  in_progress: 'En proceso',
  approved: 'Aprobado',
  cancelled: 'Cancelado'
}

export const CREDIT_ROW_STATUS_COLORS: Record<CreditRowStatus, string> = {
  not_started: 'bg-muted text-muted-foreground',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  cancelled: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300'
}

export const FINANCING_TYPE_LABELS: Record<FinancingType, string> = {
  credit: 'Crédito bancario',
  cash: 'Contado'
}

export const CREDIT_COLUMN_NAMES: Record<string, string> = {
  code: 'Negociación',
  clients: 'Cliente',
  property: 'Inmueble',
  project: 'Proyecto',
  progress: 'Etapa',
  status: 'Estado',
  financingType: 'Modalidad',
  advisor: 'Asesor',
  daysInStage: 'Días en etapa',
  startsAt: 'Inicio previsto'
}

export type CreditListRow = V2AdminCreditsListDto['data'][number]
export type CreditDetail = V2AdminCreditDetailDto
export type CreditsSummary = V2AdminCreditsSummaryDto

/** Fecha de calendario (`YYYY-MM-DD`) sin convertirla a UTC. */
export function formatCreditLocalDate(value: string | null | undefined): string {
  if (!value) return '—'
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return '—'
  return `${day}/${month}/${year}`
}

/** El día de un instante: «24 ago 2026». Para hechos que se ubican en el calendario. */
export function formatCreditInstant(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('es-GT', CREDIT_DATE_PARTS)
}

/**
 * El día Y LA HORA de un instante: «24 ago 2026, 10:07».
 *
 * Para los hechos que hay que poder AUDITAR —quién marcó qué y cuándo—, donde el día solo no
 * alcanza: dos personas tocando el mismo expediente la misma tarde quedaban indistinguibles.
 *
 * ── DE DÓNDE SALE EL FORMATO ─────────────────────────────────────────────────
 *
 * No se inventa nada: es la MISMA fecha de `formatCreditInstant` (mismas partes, mismo `es-GT`)
 * más la hora en el estilo que ya usa toda la casa — `, HH:mm` en 24 h, como
 * `audit-timeline.tsx`, `version-timeline.tsx`, la revisión del portal y las secciones de SAP.
 *
 * ☠️ `hour12: false` NO es decorativo. `es-GT` por defecto resuelve a 12 h y devuelve
 * «24 ago 2026, 10:07 a. m.»: cinco caracteres más —en un chip que ya vive apretado— y el ÚNICO
 * reloj de 12 h del producto. Sin esta línea, el formato se separa del resto solo.
 */
export function formatCreditInstantTime(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString('es-GT', {
    ...CREDIT_DATE_PARTS,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

/** Las partes de la fecha, compartidas: la versión con hora NO puede escribir el día distinto. */
const CREDIT_DATE_PARTS = {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
} as const satisfies Intl.DateTimeFormatOptions
